import React, { useMemo, useState } from "react";
import { Formik, FormikHelpers } from "formik";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
    BasicModalContent,
} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/system/useMediaQuery";
import { Box, Grid, Typography, Switch, Chip, Tabs, TabList, Tab, useTheme } from "@mui/joy";
import {
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Radio,
    Typography as MuiTypography,
} from "@mui/material";
import {
    DeleteRounded,
    NotificationAddRounded,
    NotificationsActiveRounded,
} from "@mui-symbols-material/w300";
import WizardNavigation from "../Wizard/WizardNavigation";
import InstalledBaseNodePathBreadcrumb from "../InstalledBaseNodePathBreadcrumb";
import {
    InstalledBaseNotificationType,
    IotAlertSeverity,
    IotMaintenanceType,
} from "../../codegen/graphql";
import {
    buildSubscriptionInput,
    SubscriptionInputValues,
} from "../../utils/buildSubscriptionInput";
import { InstalledBaseNodeSubscriptions } from "../../graphql/queries/installedBaseNodeSubscriptions";
import { InstalledBaseIotReportSubscriptions } from "../../graphql/queries/installedBaseIotReportSubscriptions";
import { NodeSubscriptionSettingContainer } from "../../graphql/queries/nodeSubscriptionSettingContainer";
import { CreateInstalledBaseNotificationSetting } from "../../graphql/mutations/createInstalledBaseNotificationSetting";
import { UpdateInstalledBaseNotificationSetting } from "../../graphql/mutations/updateInstalledBaseNotificationSetting";
import { CreateInstalledBaseNodeSubscription } from "../../graphql/mutations/createInstalledBaseNodeSubscription";
import { UpdateInstalledBaseNodeSubscription } from "../../graphql/mutations/updateInstalledBaseNodeSubscription";
import { DeleteInstalledBaseNodeSubscription } from "../../graphql/mutations/deleteInstalledBaseNodeSubscription";
import { CreateInstalledBaseIotReportSubscription } from "../../graphql/mutations/createInstalledBaseIotReportSubscription";
import { UpdateInstalledBaseIotReportSubscription } from "../../graphql/mutations/updateInstalledBaseIotReportSubscription";
import { DeleteInstalledBaseIotReportSubscription } from "../../graphql/mutations/deleteInstalledBaseIotReportSubscription";
import { InstalledBaseRegionSubscriptions } from "../../graphql/queries/installedBaseRegionSubscriptions";
import { CreateInstalledBaseRegionSubscription } from "../../graphql/mutations/createInstalledBaseRegionSubscription";
import { UpdateInstalledBaseRegionSubscription } from "../../graphql/mutations/updateInstalledBaseRegionSubscription";
import { DeleteInstalledBaseRegionSubscription } from "../../graphql/mutations/deleteInstalledBaseRegionSubscription";
import InfrastructureBrowser from "../InfrastructureBrowser";
import { RegionOption } from "../../hooks/useRegionSubscriptionAccess";
import useSlaSubscriptionSettings from "../../hooks/useSlaSubscriptionSettings";

interface Props {
    open: boolean;
    onClose: () => void;
    /**
     * Preselected installed-base node id. When set, the picker is hidden and
     * the modal pre-fills/edits the subscription for this node.
     */
    installedBaseNodeId?: string;
    /**
     * Set true to show the InfrastructureBrowser node picker inside the modal.
     * Used by the Subscriptions list "Add" flow. Ignored if installedBaseNodeId is provided.
     */
    allowNodePicker?: boolean;
    /**
     * Owner client id when the target node is a client root. Enables the
     * "IoT reports" section: the IoT report subscription for this client is
     * managed alongside the node subscription, sharing the same channels.
     * Only passed from the installed base tree (nodeActions).
     */
    iotReportClientId?: string;
    /**
     * Set true to additionally offer region subscriptions via tabs above the
     * picker (global/region admins only). Requires `regions`. Ignored if
     * installedBaseNodeId or regionId is provided.
     */
    allowRegionPicker?: boolean;
    /** Selectable regions for the region picker tab. */
    regions?: RegionOption[];
    /**
     * Preselected region id. When set, the modal pre-fills/edits the region
     * subscription for this region (no picker, no tabs).
     */
    regionId?: string;
}

interface FormValues extends SubscriptionInputValues {
    installedBaseNodeId: string | null;
    regionId: string | null;
    inApp: boolean;
    iotReport: boolean;
}

// UI-only sentinel for the maintenance type select: MUI Select cannot hold
// `null` (and an empty string leaves the label in placeholder position), so
// "all kinds" is mapped to this value at the select boundary only.
const ALL_MAINTENANCE_TYPES = "ALL";

// Add flow: step 1 picks the target, step 2 holds the settings.
const WIZARD_STEPS = 2;

const NodeSubscriptionModal = ({
    open,
    onClose,
    installedBaseNodeId,
    allowNodePicker,
    iotReportClientId,
    allowRegionPicker,
    regions,
    regionId,
}: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down("md"));

    // Which subscription type the modal currently targets. The tab state only
    // matters in the "Add" flow; preselected ids pin the type.
    const [pickerTab, setPickerTab] = useState<"node" | "region">("node");
    // Wizard step (add flow only); plain UI state, not part of the form.
    const [activeStep, setActiveStep] = useState(0);
    const regionFeature = !!regionId || !!allowRegionPicker;
    const subscriptionType: "node" | "region" = regionId
        ? "region"
        : installedBaseNodeId
            ? "node"
            : allowRegionPicker
                ? pickerTab
                : "node";

    const { data, loading } = useQuery(InstalledBaseNodeSubscriptions, {
        fetchPolicy: "cache-and-network",
    });

    const { data: reportData, loading: reportLoading } = useQuery(InstalledBaseIotReportSubscriptions, {
        fetchPolicy: "cache-and-network",
        skip: !iotReportClientId,
    });

    const { data: regionSubsData, loading: regionSubsLoading } = useQuery(InstalledBaseRegionSubscriptions, {
        fetchPolicy: "cache-and-network",
        skip: !regionFeature,
    });

    // SLA monitoring section: global/region admins with the SLA flag only.
    const {
        showSla,
        slaById,
        slaPending,
        refetchQueries: slaRefetchQueries,
    } = useSlaSubscriptionSettings();

    const regionSubscriptions = useMemo(
        () => ((regionSubsData?.installedBaseRegionSubscriptionList?.subscriptions ?? []) as any[]),
        [regionSubsData]
    );

    // Container lookup only feeds ensureSettingContainer. The federated
    // settings query can fail for broken accounts — tolerated (see below).
    const { data: containerData, error: containerError } = useQuery(NodeSubscriptionSettingContainer);

    const settingContainer = useMemo(() => {
        const list = containerData?.notificationSettings?.installedBase ?? [];
        return list.find(
            (s: any) =>
                s?.__typename === "InstalledBaseNodeSubscriptionNotificationSetting"
        );
    }, [containerData]);

    const existingSubscription = useMemo(() => {
        if (!installedBaseNodeId) return null;
        const subs = (data?.installedBaseNodeSubscriptionList?.subscriptions ?? []) as any[];
        return subs.find(
            (sub: any) => sub?.installedBaseNode?.id === installedBaseNodeId
        ) ?? null;
    }, [installedBaseNodeId, data]);

    const existingReportSubscription = useMemo(() => {
        if (!iotReportClientId) return null;
        const subs = (reportData?.installedBaseIotReportSubscriptionList?.subscriptions ?? []) as any[];
        return subs.find((sub: any) => sub?.client?.id === iotReportClientId) ?? null;
    }, [iotReportClientId, reportData]);

    // Existing region subscription for the PRESELECTED region (edit flow).
    // The add-flow upsert looks the selected region up on submit instead.
    const existingRegionSubscription = useMemo(() => {
        if (!regionId) return null;
        return regionSubscriptions.find(
            (sub: any) => String(sub?.region?.id) === String(regionId)
        ) ?? null;
    }, [regionId, regionSubscriptions]);

    const mode: "create" | "edit" =
        existingSubscription || existingReportSubscription || existingRegionSubscription
            ? "edit"
            : "create";

    const [createSettingMutation] = useMutation(CreateInstalledBaseNotificationSetting);
    const [updateSettingMutation] = useMutation(UpdateInstalledBaseNotificationSetting);
    const [createSubscriptionMutation] = useMutation(CreateInstalledBaseNodeSubscription);
    const [updateSubscriptionMutation] = useMutation(UpdateInstalledBaseNodeSubscription);
    const [deleteSubscriptionMutation, { loading: deleting }] = useMutation(DeleteInstalledBaseNodeSubscription);
    const [createReportSubscriptionMutation] = useMutation(CreateInstalledBaseIotReportSubscription);
    const [updateReportSubscriptionMutation] = useMutation(UpdateInstalledBaseIotReportSubscription);
    const [deleteReportSubscriptionMutation, { loading: deletingReport }] = useMutation(DeleteInstalledBaseIotReportSubscription);
    const [createRegionSubscriptionMutation] = useMutation(CreateInstalledBaseRegionSubscription);
    const [updateRegionSubscriptionMutation] = useMutation(UpdateInstalledBaseRegionSubscription);
    const [deleteRegionSubscriptionMutation, { loading: deletingRegion }] = useMutation(DeleteInstalledBaseRegionSubscription);

    // Delete clears everything managed by this modal: the node subscription,
    // — for client roots — the IoT report subscription, or the region
    // subscription in the region edit flow.
    const handleDelete = async () => {
        if (
            !existingSubscription?.id &&
            !existingReportSubscription?.id &&
            !existingRegionSubscription?.id
        ) return;
        try {
            if (existingRegionSubscription?.id) {
                const res = await deleteRegionSubscriptionMutation({
                    variables: { id: existingRegionSubscription.id },
                    refetchQueries: ["InstalledBaseRegionSubscriptions"],
                });
                if (res.data?.deleteInstalledBaseRegionSubscription?.success === false) {
                    toast.error(
                        res.data.deleteInstalledBaseRegionSubscription.message ||
                            t("Failed to delete subscription.")
                    );
                    return;
                }
            }
            if (existingSubscription?.id) {
                const res = await deleteSubscriptionMutation({
                    variables: { id: existingSubscription.id },
                    refetchQueries: ["InstalledBaseNodeSubscriptions"],
                });
                if (res.data?.deleteInstalledBaseNodeSubscription?.success === false) {
                    toast.error(
                        res.data.deleteInstalledBaseNodeSubscription.message ||
                            t("Failed to delete subscription.")
                    );
                    return;
                }
            }
            if (existingReportSubscription?.id) {
                const res = await deleteReportSubscriptionMutation({
                    variables: { id: existingReportSubscription.id },
                    refetchQueries: ["InstalledBaseIotReportSubscriptions"],
                });
                if (res.data?.deleteInstalledBaseIotReportSubscription?.success === false) {
                    toast.error(
                        res.data.deleteInstalledBaseIotReportSubscription.message ||
                            t("Failed to delete subscription.")
                    );
                    return;
                }
            }
            toast.success(t("Subscription removed."));
            onClose();
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
        }
    };

    // Channels are shared between both subscription types; on divergence the
    // node subscription wins (saving harmonizes both).
    const channelSource =
        existingSubscription ?? existingRegionSubscription ?? existingReportSubscription;
    // IoT alert and maintenance-due opt-ins live on node and region
    // subscriptions only (not on the IoT report subscription).
    const optInSource = existingSubscription ?? existingRegionSubscription;

    const initialValues: FormValues = {
        installedBaseNodeId: installedBaseNodeId ?? null,
        regionId: regionId ?? null,
        inApp: channelSource?.inApp ?? true,
        mail: channelSource?.mail ?? false,
        push: channelSource?.push ?? false,
        iotAlert: optInSource?.iotAlert ?? false,
        iotAlertSeverity:
            optInSource?.iotAlertSeverity ?? IotAlertSeverity.WARNING,
        maintenanceDue: optInSource?.maintenanceDue ?? false,
        // Severity/type stay null on the backend while the opt-in is off —
        // fall back to the recommended defaults so the selects are always
        // controlled and a fresh opt-in is valid without further input.
        maintenanceSeverity:
            optInSource?.maintenanceSeverity ?? IotAlertSeverity.WARNING,
        maintenanceType: optInSource?.maintenanceType ?? null,
        iotReport: !!existingReportSubscription,
        slaStatusChange: showSla
            ? (existingSubscription?.id
                  ? slaById.node[String(existingSubscription.id)]
                  : existingRegionSubscription?.id
                      ? slaById.region[String(existingRegionSubscription.id)]
                      : undefined) ?? false
            : undefined,
    };

    const ensureSettingContainer = async (): Promise<string | null> => {
        if (settingContainer?.id) return settingContainer.id;
        // When the settings query failed we cannot tell whether a container
        // already exists — skip the explicit create instead of risking a
        // duplicate; the backend creates the container on demand with the
        // first subscription anyway.
        if (containerError || !containerData) return null;
        const created = await createSettingMutation({
            variables: {
                notificationType:
                    InstalledBaseNotificationType.INSTALLED_BASE_NODE_SUBSCRIPTION,
            },
        });
        const newId = created.data?.createInstalledBaseNotificationSetting?.id;
        if (newId) {
            // Enable all channels at container level so per-node overrides
            // actually take effect.
            await updateSettingMutation({
                variables: {
                    id: newId,
                    input: {
                        active: true,
                        inApp: true,
                        mail: true,
                        push: true,
                    },
                },
            });
        }
        return newId ?? null;
    };

    const onSubmit = async (
        values: FormValues,
        helpers: FormikHelpers<FormValues>
    ) => {
        try {
            if (subscriptionType === "region") {
                if (!values.regionId) {
                    toast.error(t("Please pick a region first."));
                    helpers.setSubmitting(false);
                    return;
                }
                const regionInput = buildSubscriptionInput(values);
                // Upsert: picking an already subscribed region updates it
                // instead of creating a duplicate.
                const existing = regionSubscriptions.find(
                    (sub: any) => String(sub?.region?.id) === String(values.regionId)
                );
                if (existing?.id) {
                    await updateRegionSubscriptionMutation({
                        variables: { id: existing.id, input: regionInput },
                        refetchQueries: ["InstalledBaseRegionSubscriptions", ...slaRefetchQueries],
                    });
                } else {
                    await createRegionSubscriptionMutation({
                        variables: { regionId: values.regionId, input: regionInput },
                        refetchQueries: ["InstalledBaseRegionSubscriptions", ...slaRefetchQueries],
                    });
                }
                toast.success(
                    existing?.id
                        ? t("Subscription updated.")
                        : t("Subscription created.")
                );
                onClose();
                return;
            }

            if (!values.installedBaseNodeId) {
                toast.error(t("Please pick an installed base node first."));
                helpers.setSubmitting(false);
                return;
            }
            const subscriptionInput = buildSubscriptionInput(values);

            if (existingSubscription?.id) {
                await updateSubscriptionMutation({
                    variables: {
                        id: existingSubscription.id,
                        input: subscriptionInput,
                    },
                    refetchQueries: ["InstalledBaseNodeSubscriptions", ...slaRefetchQueries],
                });
            } else {
                await ensureSettingContainer();
                await createSubscriptionMutation({
                    variables: {
                        installedBaseNodeId: values.installedBaseNodeId,
                        input: subscriptionInput,
                    },
                    refetchQueries: ["InstalledBaseNodeSubscriptions", ...slaRefetchQueries],
                });
            }

            // Client roots: keep the IoT report subscription in sync — same
            // channels, existence driven by the "Notify on IoT reports" toggle.
            // The report setting container is auto-created by the backend.
            if (iotReportClientId) {
                const reportInput = {
                    inApp: true,
                    mail: values.mail,
                    push: values.push,
                };
                if (values.iotReport && existingReportSubscription?.id) {
                    await updateReportSubscriptionMutation({
                        variables: { id: existingReportSubscription.id, input: reportInput },
                        refetchQueries: ["InstalledBaseIotReportSubscriptions"],
                    });
                } else if (values.iotReport) {
                    await createReportSubscriptionMutation({
                        variables: { clientId: iotReportClientId, input: reportInput },
                        refetchQueries: ["InstalledBaseIotReportSubscriptions"],
                    });
                } else if (existingReportSubscription?.id) {
                    await deleteReportSubscriptionMutation({
                        variables: { id: existingReportSubscription.id },
                        refetchQueries: ["InstalledBaseIotReportSubscriptions"],
                    });
                }
            }

            toast.success(
                mode === "edit"
                    ? t("Subscription updated.")
                    : t("Subscription created.")
            );
            onClose();
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
        } finally {
            helpers.setSubmitting(false);
        }
    };

    const showTabs =
        !!allowRegionPicker &&
        !installedBaseNodeId &&
        !regionId &&
        (regions?.length ?? 0) > 0;
    const showNodePicker =
        subscriptionType === "node" && allowNodePicker && !installedBaseNodeId;
    const showRegionPicker = subscriptionType === "region" && !regionId;
    // Only the list's "Add" flow picks its target inside the modal — that is
    // the two-step wizard. Every other entry point (tree, detail panel,
    // dashboards, edit from the list) presets the target and shows the
    // settings step only.
    const isWizard =
        !installedBaseNodeId && !regionId && (!!allowNodePicker || !!allowRegionPicker);

    // Selecting an already subscribed region pre-fills its current values so
    // saving acts as an update (upsert) instead of blindly overwriting.
    const handleRegionSelect = (
        id: string,
        setFieldValue: (field: string, value: any) => void
    ) => {
        setFieldValue("regionId", id);
        const existing = regionSubscriptions.find(
            (sub: any) => String(sub?.region?.id) === id
        );
        if (existing) {
            setFieldValue("mail", !!existing.mail);
            setFieldValue("push", !!existing.push);
            setFieldValue("iotAlert", !!existing.iotAlert);
            setFieldValue(
                "iotAlertSeverity",
                existing.iotAlertSeverity ?? IotAlertSeverity.WARNING
            );
            setFieldValue("maintenanceDue", !!existing.maintenanceDue);
            setFieldValue(
                "maintenanceSeverity",
                existing.maintenanceSeverity ?? IotAlertSeverity.WARNING
            );
            setFieldValue("maintenanceType", existing.maintenanceType ?? null);
            if (showSla) {
                setFieldValue("slaStatusChange", slaById.region[String(existing.id)] ?? false);
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen={isFullScreen} fullWidth maxWidth="sm">
            <BasicModalContent
                startIcon={mode === "edit" ? <NotificationsActiveRounded /> : <NotificationAddRounded />}
                onClose={onClose}
                title={
                    <Typography level={"titleSmallLight"}>
                        {mode === "edit"
                            ? t("Edit notification subscription")
                            : t("Notification subscription")}
                    </Typography>
                }
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                    enableReinitialize
                >
                    {({ values, setFieldValue, isSubmitting, handleSubmit, submitForm }) => {
                        const busy = isSubmitting || deleting || deletingReport || deletingRegion;
                        const hasTarget =
                            subscriptionType === "node"
                                ? !!values.installedBaseNodeId
                                : !!values.regionId;
                        const regionSubsPending = regionFeature && regionSubsLoading && !regionSubsData;
                        const saveDisabled =
                            busy ||
                            loading ||
                            // Don't save before knowing whether a report/region
                            // subscription exists (create vs update).
                            (!!iotReportClientId && reportLoading && !reportData) ||
                            regionSubsPending ||
                            // Saving before the SLA opt-in is known would reset it.
                            slaPending ||
                            !hasTarget;
                        // Step 1 → 2 also waits for the region subscriptions on the
                        // region tab so the upsert prefill has data to work with.
                        const nextDisabled =
                            !hasTarget || (subscriptionType === "region" && regionSubsPending);
                        const canDelete =
                            mode === "edit" &&
                            !!(
                                existingSubscription?.id ||
                                existingReportSubscription?.id ||
                                existingRegionSubscription?.id
                            );
                        const selectedRegionName = (regions ?? []).find(
                            (region) => region.id === values.regionId
                        )?.name;

                        // Wizard step 1: pick the target (tabs + node/region picker).
                        const targetStep = (
                            <>
                                {showTabs && (
                                    <Tabs
                                        value={pickerTab}
                                        onChange={(_, v) =>
                                            setPickerTab((v as "node" | "region") ?? "node")
                                        }
                                        size="lg"
                                        sx={{ mb: 2, backgroundColor: "transparent" }}
                                    >
                                        {/* Styled like the open-asset tabs (AutoScrollTabs). */}
                                        <TabList disableUnderline tabFlex={1}>
                                            {[
                                                { value: "node", label: t("Installed Base") },
                                                { value: "region", label: t("Region") },
                                            ].map((tab) => (
                                                <Tab
                                                    key={tab.value}
                                                    value={tab.value}
                                                    disableIndicator
                                                    variant="plain"
                                                    color="neutral"
                                                    sx={{
                                                        minWidth: 100,
                                                        textTransform: "none",
                                                        marginRight: "2px",
                                                        borderBottom:
                                                            pickerTab === tab.value
                                                                ? `2px solid ${theme.vars.palette.primary[500]}`
                                                                : "2px solid #ccc",
                                                        backgroundColor: "transparent",
                                                        '&[aria-selected="true"]': {
                                                            backgroundColor: "transparent",
                                                        },
                                                        '&:not([aria-selected="true"]):hover': {
                                                            backgroundColor: "transparent",
                                                        },
                                                    }}
                                                >
                                                    {tab.label}
                                                </Tab>
                                            ))}
                                        </TabList>
                                    </Tabs>
                                )}
                                {showNodePicker && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography level="title-sm" sx={{ pb: 1 }}>
                                            {t("Installed base node")}
                                        </Typography>
                                        <InfrastructureBrowser
                                            value={values.installedBaseNodeId ?? undefined}
                                            onChange={(id) =>
                                                setFieldValue("installedBaseNodeId", id)
                                            }
                                        />
                                    </Box>
                                )}
                                {showRegionPicker && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography level="title-sm" sx={{ pb: 1 }}>
                                            {t("Region")}
                                        </Typography>
                                        {/* Container + rows mirror the InfrastructureBrowser
                                            styles so both picker tabs look alike. */}
                                        <Box
                                            sx={{
                                                border: "1px solid #ccc",
                                                borderRadius: "5px",
                                                overflow: "hidden",
                                                backgroundColor: "background.paper",
                                            }}
                                        >
                                            <Box sx={{ height: 240, overflowY: "auto" }}>
                                                {(regions ?? []).map((region, index) => {
                                                    const isSelected = values.regionId === region.id;
                                                    const isLast = index === (regions?.length ?? 0) - 1;
                                                    const subscribed = regionSubscriptions.some(
                                                        (sub: any) =>
                                                            String(sub?.region?.id) === region.id
                                                    );
                                                    return (
                                                        <Box
                                                            key={region.id}
                                                            onClick={() =>
                                                                handleRegionSelect(region.id, setFieldValue)
                                                            }
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                                px: 1.5,
                                                                py: 1,
                                                                cursor: "pointer",
                                                                borderBottom: isLast ? "none" : "1px solid #eee",
                                                                backgroundColor: isSelected
                                                                    ? "#E5E5E5"
                                                                    : "transparent",
                                                                "&:hover": {
                                                                    backgroundColor: isSelected
                                                                        ? "#E5E5E5"
                                                                        : "rgba(0, 0, 0, 0.04)",
                                                                },
                                                            }}
                                                        >
                                                            <Radio
                                                                checked={isSelected}
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRegionSelect(region.id, setFieldValue);
                                                                }}
                                                                sx={{ p: 0.5 }}
                                                                inputProps={{ "aria-label": region.name }}
                                                            />
                                                            <MuiTypography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 500,
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {region.name}
                                                            </MuiTypography>
                                                            {subscribed && (
                                                                <Chip size="sm" variant="soft" color="neutral" sx={{ flexShrink: 0 }}>
                                                                    {t("Subscribed")}
                                                                </Chip>
                                                            )}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                            </>
                        );

                        // Wizard step 2 context line: the chosen target, like the
                        // asset header of the event wizard.
                        const targetHeader = (
                            <Box sx={{ mt: 0.5, mb: 1 }}>
                                {subscriptionType === "node" ? (
                                    <InstalledBaseNodePathBreadcrumb
                                        nodeId={values.installedBaseNodeId ?? undefined}
                                    />
                                ) : (
                                    // Same band as the node path breadcrumb so both
                                    // targets look alike.
                                    <Box
                                        sx={{
                                            mx: "-16px",
                                            px: "16px",
                                            py: "10px",
                                            borderTop: "1px solid",
                                            borderBottom: "1px solid",
                                            borderColor: "divider",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Typography level="body-sm" sx={{ color: "#666", flexShrink: 0 }}>
                                            {t("Region")}
                                        </Typography>
                                        <Typography
                                            level="body-sm"
                                            sx={{
                                                color: "#000",
                                                fontWeight: "bold",
                                                minWidth: 0,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {selectedRegionName ?? ""}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        );

                        // The settings — wizard step 2, or the whole body when the
                        // target is preset. Keep the switch order (Web App, E-Mail,
                        // Push, IoT alerts, Maintenance due, SLA monitoring, IoT
                        // reports last): the e2e specs address the switches by
                        // position (and the IoT reports switch as the last one).
                        const settings = (
                            <>
                                <Box sx={{ marginBottom: "8px", borderBottom: "1px solid #eee" }}>
                                    <Typography
                                        level="title-sm"
                                        sx={{ paddingBottom: "8px" }}
                                    >
                                        {t("Channels")}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <Switch checked disabled />
                                        <Box>
                                            <Typography level="body-md">
                                                {t("Web App")}
                                            </Typography>
                                            <Typography level="body-xs" textColor="text.tertiary">
                                                {t("Receive notifications via the web app")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <Switch
                                            checked={values.mail}
                                            onChange={(e) =>
                                                setFieldValue("mail", e.target.checked)
                                            }
                                        />
                                        <Box>
                                            <Typography level="body-md">{t("E-Mail")}</Typography>
                                            <Typography level="body-xs" textColor="text.tertiary">
                                                {t("Receive notifications via e-mail")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <Switch
                                            checked={values.push}
                                            onChange={(e) =>
                                                setFieldValue("push", e.target.checked)
                                            }
                                        />
                                        <Box>
                                            <Typography level="body-md">
                                                {t("Busch Pfeiffer App")}
                                            </Typography>
                                            <Typography level="body-xs" textColor="text.tertiary">
                                                {t("Receive push notifications on your phone")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ marginBottom: "8px", borderBottom: "1px solid #eee" }}>
                                    <Typography
                                        level="title-sm"
                                        sx={{ paddingBottom: "8px" }}
                                    >
                                        {t("IoT alerts")}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <Switch
                                            checked={values.iotAlert}
                                            onChange={(e) =>
                                                setFieldValue("iotAlert", e.target.checked)
                                            }
                                        />
                                        <Typography level="body-md">
                                            {t("Notify on IoT alerts")}
                                        </Typography>
                                    </Box>
                                    <FormControl
                                        size="small"
                                        disabled={!values.iotAlert}
                                        sx={{ mt: 1.5, maxWidth: 240 }}
                                    >
                                        <InputLabel id="severity-label">
                                            {t("Minimum severity")}
                                        </InputLabel>
                                        <Select
                                            labelId="severity-label"
                                            label={t("Minimum severity")}
                                            value={values.iotAlertSeverity}
                                            disabled={!values.iotAlert}
                                            onChange={(e) =>
                                                setFieldValue(
                                                    "iotAlertSeverity",
                                                    e.target.value as IotAlertSeverity
                                                )
                                            }
                                        >
                                            <MenuItem value={IotAlertSeverity.WARNING}>
                                                {t("Warning")}
                                            </MenuItem>
                                            <MenuItem value={IotAlertSeverity.ERROR}>
                                                {t("Alarm")}
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Keep this section between "IoT alerts" and "IoT reports":
                                    the e2e specs address the switches by position. */}
                                <Box sx={{ marginBottom: "8px", borderBottom: "1px solid #eee" }}>
                                    <Typography
                                        level="title-sm"
                                        sx={{ paddingBottom: "8px" }}
                                    >
                                        {t("Maintenance Due")}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <Switch
                                            checked={values.maintenanceDue}
                                            onChange={(e) =>
                                                setFieldValue("maintenanceDue", e.target.checked)
                                            }
                                        />
                                        <Typography level="body-md">
                                            {t("Notify when maintenance is due")}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1.5 }}>
                                        {/* Threshold, not an exact match: WARNING also delivers
                                            the later escalation to ERROR (overdue). */}
                                        <FormControl
                                            size="small"
                                            disabled={!values.maintenanceDue}
                                            sx={{ flex: "1 1 240px", maxWidth: 280 }}
                                        >
                                            <InputLabel id="maintenance-severity-label">
                                                {t("Minimum severity")}
                                            </InputLabel>
                                            <Select
                                                labelId="maintenance-severity-label"
                                                label={t("Minimum severity")}
                                                value={values.maintenanceSeverity}
                                                disabled={!values.maintenanceDue}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        "maintenanceSeverity",
                                                        e.target.value as IotAlertSeverity
                                                    )
                                                }
                                            >
                                                <MenuItem value={IotAlertSeverity.WARNING}>
                                                    {t("From warning (recommended)")}
                                                </MenuItem>
                                                <MenuItem value={IotAlertSeverity.ERROR}>
                                                    {t("Only when overdue")}
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                        <FormControl
                                            size="small"
                                            disabled={!values.maintenanceDue}
                                            sx={{ flex: "1 1 200px", maxWidth: 240 }}
                                        >
                                            <InputLabel id="maintenance-type-label">
                                                {t("Maintenance type")}
                                            </InputLabel>
                                            <Select
                                                labelId="maintenance-type-label"
                                                label={t("Maintenance type")}
                                                value={values.maintenanceType ?? ALL_MAINTENANCE_TYPES}
                                                disabled={!values.maintenanceDue}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        "maintenanceType",
                                                        e.target.value === ALL_MAINTENANCE_TYPES
                                                            ? null
                                                            : (e.target.value as IotMaintenanceType)
                                                    )
                                                }
                                            >
                                                <MenuItem value={ALL_MAINTENANCE_TYPES}>
                                                    {t("All types")}
                                                </MenuItem>
                                                <MenuItem value={IotMaintenanceType.MAINTENANCE}>
                                                    {t("Maintenance")}
                                                </MenuItem>
                                                <MenuItem value={IotMaintenanceType.REMANUFACTURE}>
                                                    {t("Remanufacturing")}
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Box>

                                {/* Admin-only, so it sits behind the switches the e2e
                                    specs address by index; IoT reports stays last. */}
                                {showSla && (
                                    <>
                                        <Box sx={{ marginBottom: "8px", borderBottom: "1px solid #eee" }}>
                                            <Typography
                                                level="title-sm"
                                                sx={{ paddingBottom: "8px" }}
                                            >
                                                {t("SLA monitoring")}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                <Switch
                                                    checked={!!values.slaStatusChange}
                                                    onChange={(e) =>
                                                        setFieldValue("slaStatusChange", e.target.checked)
                                                    }
                                                />
                                                <Typography level="body-md">
                                                    {t("Notify on SLA status change")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </>
                                )}

                                {iotReportClientId && (
                                    <>
                                        <Box sx={{ marginBottom: "8px", borderBottom: "1px solid #eee" }}>
                                            <Typography
                                                level="title-sm"
                                                sx={{ paddingBottom: "8px" }}
                                            >
                                                {t("IoT reports")}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                <Switch
                                                    checked={values.iotReport}
                                                    onChange={(e) =>
                                                        setFieldValue("iotReport", e.target.checked)
                                                    }
                                                />
                                                <Typography level="body-md">
                                                    {t("Notify on IoT reports")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </>
                                )}

                            </>
                        );

                        return (
                            <form
                                onSubmit={(e) => {
                                    // Step 1 has no submit button, but Enter in the
                                    // browser's search field would still submit the
                                    // form and create a subscription with default
                                    // settings — swallow it there.
                                    if (isWizard && activeStep === 0) {
                                        e.preventDefault();
                                        return;
                                    }
                                    handleSubmit(e);
                                }}
                            >
                                {/* Outside the padded box: the breadcrumb band bleeds
                                    to the modal edges (mx: -16px), like in the edit
                                    node modal and the duplicate wizard. */}
                                {isWizard && activeStep === 1 && targetHeader}
                                <Box sx={{ padding: "8px 16px 16px 16px", minWidth: 320 }}>
                                    {isWizard && activeStep === 0 && (
                                        <>
                                            {targetStep}
                                            <Box sx={{ pt: 2 }}>
                                                <WizardNavigation
                                                    activeStep={0}
                                                    totalSteps={WIZARD_STEPS}
                                                    onBack={() => undefined}
                                                    onNext={() => setActiveStep(1)}
                                                    onFinish={() => undefined}
                                                    isNextDisabled={nextDisabled}
                                                />
                                            </Box>
                                        </>
                                    )}
                                    {isWizard && activeStep === 1 && (
                                        <>
                                            {settings}
                                            <Box sx={{ pt: 1 }}>
                                                <WizardNavigation
                                                    activeStep={1}
                                                    totalSteps={WIZARD_STEPS}
                                                    onBack={() => setActiveStep(0)}
                                                    onNext={() => undefined}
                                                    onFinish={() => {
                                                        void submitForm();
                                                    }}
                                                    isFinishDisabled={saveDisabled}
                                                    isFinishLoading={isSubmitting}
                                                    finishButtonText={t("Save")}
                                                />
                                            </Box>
                                        </>
                                    )}
                                    {!isWizard && (
                                        <>
                                            {settings}
                                            {/* Equal-width button row like the other modals
                                                (ConfirmDeleteModal, TDAForm): thirds with
                                                Delete in edit mode, halves otherwise. */}
                                            <Grid container spacing={1} sx={{ pt: 1 }}>
                                                {canDelete && (
                                                    <Grid xs={4}>
                                                        <Button
                                                            fullWidth
                                                            variant="outlined"
                                                            color="error"
                                                            startIcon={<DeleteRounded />}
                                                            onClick={handleDelete}
                                                            disabled={busy}
                                                        >
                                                            {t("Delete")}
                                                        </Button>
                                                    </Grid>
                                                )}
                                                <Grid xs={canDelete ? 4 : 6}>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        onClick={onClose}
                                                        disabled={busy}
                                                    >
                                                        {t("Cancel")}
                                                    </Button>
                                                </Grid>
                                                <Grid xs={canDelete ? 4 : 6}>
                                                    <Button
                                                        fullWidth
                                                        type="submit"
                                                        variant="contained"
                                                        disabled={saveDisabled}
                                                    >
                                                        {t("Save")}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}
                                </Box>
                            </form>
                        );
                    }}
                </Formik>
            </BasicModalContent>
        </Dialog>
    );
};

export default NodeSubscriptionModal;
